import type { ComponentDocs } from '../component-docs.js';

/**
 * What `<caos-workspace-rail>` says about itself.
 *
 * It is not the app launcher. The launcher opens the apps of one workspace; this switches between
 * workspaces — a different set of conversations, people and counts. Both can be on screen at once.
 *
 * A tile is one or two LETTERS, not an image. A rail of images is empty while they load and broken
 * when one fails, and the whole value of a rail is hitting the right tile without reading it. The
 * workspace's name is still the accessible name and the tooltip, so nothing rests on recognising a
 * letter.
 *
 * Choosing a tile reports and marks nothing. `current` is set by whatever switched workspace, after
 * it switched.
 */
const docs: ComponentDocs = {
  aliases: ['workspace switcher', 'org switcher', 'team rail', 'tenant switcher', 'space rail'],

  answers: [
    'switch between workspaces',
    'show which workspace is open',
    'show which other workspace has something waiting',
  ],

  placements: ['recordPage', 'homePage', 'appPage'],

  inputs: [
    {
      name: 'current',
      attribute: 'current',
      type: 'text',
      description:
        'Which workspace is open, by id. Set it AFTER the switch — this component does not set it on ' +
        'click, so a failed switch does not leave the rail claiming otherwise.',
    },
  ],

  events: [
    {
      name: 'select',
      carries: '{ id: string }',
      when:
        'Somebody chose a workspace. Nothing has changed yet: switching, and then setting `current`, is ' +
        'the surface’s.',
    },
  ],

  state: [],
  slots: [],

  tokens: [
    { token: '--caos-color-surface-2', controls: 'The rail itself, so it reads as a strip beside the workspace.' },
    { token: '--caos-color-surface', controls: 'A tile that is not the current one.' },
    { token: '--caos-color-border', controls: 'Each tile’s edge, and the rail’s edge against what is beside it.' },
    { token: '--caos-color-text-muted', controls: 'The letters on a tile that is not current.' },
    { token: '--caos-color-text', controls: 'The letters on a tile under the pointer.' },
    { token: '--caos-color-accent-fill', controls: 'The current workspace’s tile.' },
    { token: '--caos-color-accent-contrast', controls: 'The letters drawn on that fill, and on a count.' },
    { token: '--caos-color-danger-fill', controls: 'The count on a workspace with something waiting — the one place in the set that is not the accent, because it is a thing somebody is missing rather than a thing they chose.' },
    { token: '--caos-color-danger-contrast', controls: 'The number drawn on that fill.' },
    { token: '--caos-font', controls: 'The typeface of the letters.' },
    { token: '--caos-text-label', controls: 'The size of the letters.' },
    { token: '--caos-font-weight-label', controls: 'The weight of the letters.' },
    { token: '--caos-space-2', controls: 'The gap between tiles and the padding of the rail.' },
    { token: '--caos-radius-sm', controls: 'The roundness of a tile.' },
    { token: '--caos-radius-pill', controls: 'The roundness of a count.' },
    { token: '--caos-focus-ring', controls: 'The ring on a tile.' },
  ],

  parts: [
    { part: 'rail', is: 'the strip' },
    { part: 'tile', is: 'one workspace' },
    { part: 'count', is: 'the number on a workspace with something waiting' },
  ],

  methods: [],

  placing:
    'As the outermost column of a conversation surface, to the leading side of everything else. It ' +
    'fills the height it is given and scrolls if there are more workspaces than fit; it draws its own ' +
    'edge against what is beside it.',

  whenToUse:
    'Somebody belongs to more than one workspace and moves between them often enough that a menu would ' +
    'be in the way. For two, or for a switch made once a week, a menu entry is less furniture.',

  insteadUse: [
    {
      name: 'AppLauncher',
      when:
        'The choice is which APP to open inside the workspace already open. That is the level below ' +
        'this one, and the two are not alternatives — a screen can have both.',
    },
    {
      name: 'Topbar',
      when:
        'The switch belongs in the account menu because it happens rarely. A permanent strip for a ' +
        'rare choice is a strip that is in the way for most of the day.',
    },
    {
      name: 'ChannelList',
      when: 'The choice is which conversation inside one workspace, which is what that list is for.',
    },
  ],

  accessibility:
    'The rail is a `role="navigation"` holding a named list, and the current workspace carries ' +
    '`aria-current`. Each tile’s accessible name is the workspace’s NAME, never its letters — the ' +
    'letters are `aria-hidden`, because read out they say the same thing twice and the second time as ' +
    'nonsense. When something is waiting, the count is folded into that name ("Design, 4 unread") ' +
    'rather than left as a hidden decoration, so the same two facts reach a reader who cannot see the ' +
    'number on the tile. Left to the author: `aria-label` on the element when a screen carries more ' +
    'than one navigation strip.',

  examples: [
    {
      key: 'rail',
      label: 'Three workspaces, one of them waiting',
      summary: 'The current one is marked; another carries a count.',
      placement: { component: 'caos-workspace-rail', inputs: { current: 'platform', 'aria-label': 'Workspaces' } },
    },
  ],
};

export default docs;
